import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  ARTIFACT_TYPES,
  createProcessSchema,
  ENTITY_STATUS,
  type CreateProcessInput,
} from "@aos/shared";
import { FormShell } from "../skills/_components/FormShell";
import {
  NumberField,
  optionsFromEnum,
  SelectField,
  TextAreaField,
  TextField,
} from "../skills/_components/form";

export interface ProcessFormProps {
  action: "create" | "edit";
}

/** Create/edit form for a Process. Validated against `createProcessSchema`. */
export function ProcessForm({ action }: ProcessFormProps) {
  const form = useForm<CreateProcessInput>({
    resolver: zodResolver(createProcessSchema),
    refineCoreProps: { resource: "processes", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
  } = form;

  return (
    <FormShell
      title={action === "create" ? "New Process" : "Edit Process"}
      subtitle="Define the contour's purpose, domain, artifact, and rigidity."
      resource="processes"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField form={form} name="name" label="Name" placeholder="Process name" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="domain"
          label="Domain"
          placeholder="e.g. operations"
        />
        <SelectField
          form={form}
          name="artifact_type"
          label="Artifact Type"
          options={optionsFromEnum(ARTIFACT_TYPES)}
          allowEmpty
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <NumberField
          form={form}
          name="rigidity_level"
          label="Rigidity Level"
          placeholder="3"
        />
        <SelectField
          form={form}
          name="status"
          label="Status"
          options={optionsFromEnum(ENTITY_STATUS)}
        />
      </div>
      <TextAreaField
        form={form}
        name="purpose"
        label="Purpose"
        placeholder="What this process is for"
      />
    </FormShell>
  );
}

export default ProcessForm;
